import React, { useState, useEffect } from 'react';
import { Input, FormControl, Box, useToast, Text, Progress, Flex, CloseButton, Button } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { gql, useMutation } from '@apollo/client';
import c from '../../constant';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const CREATE_TEMP_FILE_MUTATION = gql`
	mutation uploadTempFile($type: String!, $fileSize: Float!, $file: Upload!) {
		userUploadTempFile(type: $type, fileSize: $fileSize, file: $file) {
			id
		}
	}
`;

const TempFileUploader = (props) => {
	const { t } = useTranslation();
	const {
		name,
		label,
		required,
		maxFileSize = 5242880,
		accept = 'application/pdf,.xlsx,.xls,image/*,.doc, .docx,.ppt, .pptx,.txt,.zip,.rar,.7zip',
		callBack,
		onCancelFile,
		mt = 1,
		mb = 1,
		pb = 1,
		pt = 1,
		size = 'md',
		type = 'np',
	} = props;
	const toast = useToast();
	const [fileName, setFileName] = useState('');
	const [uploadFile, { error, data }] = useMutation(CREATE_TEMP_FILE_MUTATION);

	useEffect(() => {
		if (data?.userUploadTempFile) {
			setUploading(false);
			setProgress(0);
			setTimeout(() => {
				callBack(data?.userUploadTempFile);
			}, 100);
		}
	}, [data]);
	const [uploading, setUploading] = useState(false);
	const [progress, setProgress] = useState(0);
	const hiddenFileInput = React.useRef(null);

	useEffect(() => {
		if (error) {
			setFileName(null);
			onCancelFile();
			setUploading(false);
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function chooseFile() {
		hiddenFileInput.current.click();
	}

	useEffect(() => {
		props?.callBackUploading(uploading);
	}, [uploading]);

	return (
		<Box mt={mt} mb={mb} pb={pb} pt={pt} borderBottom="1px solid white">
			<FormControl margin="normal">
				{label && (
					<Text mb="2">
						<pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'Rajdhani, Hanuman, sans-serif, Moul !important' }}>{label}</pre>
					</Text>
				)}
				{fileName && (
					<Flex borderRadius={c.borderRadius} bg="#ecf0f1" p="1" pl="2" pr="2" alignItems="center">
						<Flex flex="1" alignItems="center">
							<FontAwesomeIcon
								style={{
									fontSize: 20,
									marginRight: 12,
									marginLeft: 8,
									color: '#27ae60',
								}}
								icon={faFileAlt}
							/>
							<Text color="#2980b9" noOfLines={1}>
								{fileName}
							</Text>
						</Flex>
						<CloseButton
							onClick={() => {
								setFileName(null);
								onCancelFile();
							}}
							borderRadius={c.borderRadius}
							ml="2"
							color="#c0392b"
							size={size}
						/>
					</Flex>
				)}

				{!fileName && (
					<Box>
						<Button
							disabled={props?.disabled}
							borderLeft={`3px solid ${required ? 'red' : 'rgba(0,0,0,0)'}`}
							borderRadius={c.borderRadius}
							leftIcon={
								<FontAwesomeIcon
									style={{
										fontSize: 20,
										marginRight: 12,
										marginLeft: 5,
										color: '#27ae60',
									}}
									icon={faFileAlt}
								/>
							}
							onClick={() => chooseFile()}
							size={size}
							w="100%"
						>
							{t('Choose file')}
							{required && (
								<Text ml="2px" color="red">
									*
								</Text>
							)}
						</Button>

						<Input
							display="none"
							ref={hiddenFileInput}
							disabled={uploading}
							accept={accept}
							pt="4px"
							id={name}
							name={name}
							type="file"
							onChange={(e) => {
								e.preventDefault();
								let reader = new FileReader();
								let file = e.target.files[0];

								if (file) {
									reader.readAsDataURL(file);
									if (file?.size > 104857600) {
										toast({
											title: t('FILE_SIZE_EXCEEDS_LIMIT'),
											status: 'error',
											isClosable: true,
											position: 'bottom-right',
										});
										setFileName(null);
										onCancelFile();
										setUploading(false);
									} else {
										setUploading(true);
										setFileName(file?.name);
										setTimeout(() => {
											let abort;
											const variables = {
												type,
												fileSize: file?.size,
												file: file,
											};
											uploadFile({
												variables,
												context: {
													fetchOptions: {
														useUpload: true,
														onProgress: (ev) => {
															setProgress(ev.loaded / ev.total);
														},
														onAbortPossible: (abortHandler) => {
															abort = abortHandler;
														},
													},
												},
											});

											if (abort) {
												abort();
											}
										}, 100);
									}
								}
							}}
						/>
					</Box>
				)}
			</FormControl>
			{uploading && (
				<Box h={1}>
					{progress && progress < 1 ? <Progress size="xs" colorScheme="green" hasStripe value={(progress.toFixed(2) * 100).toFixed(0)} /> : null}
					{!progress || progress === 1 ? <Progress size="xs" colorScheme="green" isIndeterminate /> : null}
				</Box>
			)}
		</Box>
	);
};

export default React.memo(TempFileUploader);
